import React, { Component } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Icon,
  Typography
} from "@material-ui/core";
import PropTypes from "prop-types";
import styled from "styled-components";
import { withRouter } from "react-router-dom";
import { withAlert } from "react-alert";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import StudentService from "../../services/StudentService";
import ClassService from "../../services/ClassService";
import BackButton from "../../components/BackButton";
import Group from "../../waybee-ui/Group";
import Heading from "../../waybee-ui/Heading";
import SearchableSelect from "../../waybee-ui/SearchableSelect";
import TableGrid from "../../waybee-ui/TableGrid";
import fetchGradeJobQueue from "../../utils/fetchGradeJobQueue";
import {
  isReportGenerating,
  pruneGeneratingByReportId
} from "../../utils/reportGeneratingState";

const LoadContainer = styled.div`
  width: 100%;
  text-align: center;
  padding: 32px 8px;
`;

const RowBody = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 20px;
  width: 100%;
  padding: 4px 0;
  opacity: ${props => (props.$dimmed ? 0.7 : 1)};
`;

const TitleBlock = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 160px;
  flex: 1 1 200px;
`;

const ReportTitle = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ReportIcon = styled(Icon)`
  && {
    color: ${props => props.$color || "#0d9488"};
    font-size: 22px;
  }
`;

const ParamsBlock = styled(Box)`
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 200px;
  max-width: 280px;
  width: 100%;

  @media (min-width: 600px) {
    width: 260px;
  }
`;

function reportUiParams(report, t) {
  if (Array.isArray(report.uiParams)) return report.uiParams;
  // Fallback se API ainda não enviou uiParams
  if (report.paramMode === "ra_disc") {
    return [
      {
        key: "idTurmaDisc",
        type: "discipline",
        label: t("common.labels.discipline"),
        required: true
      }
    ];
  }
  return [];
}

function buildDefaultParamValues(reports, courses, t) {
  const firstDisc = courses && courses.length ? courses[0].idTurmaDisc : null;
  const map = {};
  (reports || []).forEach(report => {
    const params = reportUiParams(report, t);
    if (!params.length) return;
    const values = {};
    params.forEach(p => {
      if (p.type === "discipline" && firstDisc != null) {
        values[p.key] = firstDisc;
      }
    });
    if (Object.keys(values).length) {
      map[report.id] = values;
    }
  });
  return map;
}

class StudentReports extends Component {
  constructor(props) {
    super(props);
    this.state = {
      courses: [],
      loadingFilters: false,
      loadingReports: false,
      generatingByReportId: {},
      paramValuesByReportId: {},
      reports: []
    };
  }

  componentDidMount() {
    this.bootstrap();
  }

  componentDidUpdate(prevProps) {
    const { jobsState } = this.props;
    const { generatingByReportId } = this.state;
    const jobs = (jobsState && jobsState.jobs) || [];
    const previousJobs =
      (prevProps.jobsState && prevProps.jobsState.jobs) || [];
    const next = pruneGeneratingByReportId(
      generatingByReportId,
      jobs,
      previousJobs
    );
    if (next !== generatingByReportId) {
      this.setState({ generatingByReportId: next });
    }
  }

  bootstrap = async () => {
    const { alert, match, t } = this.props;
    const { studentId } = match.params;
    const classId = localStorage.getItem("class");

    this.setState({ loadingFilters: true, loadingReports: true });
    try {
      // 1) vínculo do professor na turma; 2) matrícula do aluno / FT da turma
      let courses = [];
      if (classId) {
        courses = (await ClassService.getDisciplines(classId)) || [];
      }
      if (!courses.length) {
        courses =
          (await StudentService.getDisciplines(
            studentId,
            classId || undefined
          )) || [];
      }
      this.setState({ courses });

      const reports = await StudentService.getReports(studentId);
      const list = Array.isArray(reports) ? reports : [];
      this.setState({
        reports: list,
        paramValuesByReportId: buildDefaultParamValues(list, courses, t)
      });
    } catch (e) {
      console.error(e);
      alert.show(t("student.reports.errorLoad"), {
        title: t("common.actions.error")
      });
    } finally {
      this.setState({ loadingFilters: false, loadingReports: false });
    }
  };

  setReportParam = (reportId, key, value) => {
    this.setState(prev => ({
      paramValuesByReportId: {
        ...prev.paramValuesByReportId,
        [reportId]: {
          ...(prev.paramValuesByReportId[reportId] || {}),
          [key]: value
        }
      }
    }));
  };

  missingRequiredParams = (report, values) => {
    const { t } = this.props;
    const params = reportUiParams(report, t);
    return params.filter(
      p => p.required && (values[p.key] == null || values[p.key] === "")
    );
  };

  onSelectItem = async report => {
    const { match, alert, jobsState, t } = this.props;
    const { generatingByReportId, paramValuesByReportId } = this.state;
    const jobs = (jobsState && jobsState.jobs) || [];
    if (isReportGenerating(report.id, generatingByReportId, jobs)) return;

    const values = paramValuesByReportId[report.id] || {};
    const missing = this.missingRequiredParams(report, values);
    if (missing.length) {
      alert.show(
        t("student.reports.selectParams", {
          params: missing.map(m => m.label || m.key).join(", ")
        }),
        { title: t("student.reports.requiredParam") }
      );
      return;
    }

    const { studentId } = match.params;
    const paramValues = { ...values };

    this.setState(prev => ({
      generatingByReportId: {
        ...prev.generatingByReportId,
        [report.id]: "submitting"
      }
    }));

    try {
      const created = await ClassService.createReportJob({
        ...report,
        classId: studentId,
        ra: studentId,
        paramMode: report.paramMode,
        paramValues,
        idTurmaDisc: paramValues.idTurmaDisc
      });
      const jobId = created && created.id;
      this.setState(prev => ({
        generatingByReportId: {
          ...prev.generatingByReportId,
          [report.id]: jobId || "submitting"
        }
      }));
      fetchGradeJobQueue();
    } catch (e) {
      console.error(e);
      alert.show(t("student.reports.startError"), {
        title: t("common.actions.error")
      });
      this.setState(prev => {
        const next = { ...prev.generatingByReportId };
        delete next[report.id];
        return { generatingByReportId: next };
      });
    }
  };

  renderDisciplineParam = (report, param, isGenerating) => {
    const { courses, paramValuesByReportId, loadingFilters } = this.state;
    const { t } = this.props;
    const values = paramValuesByReportId[report.id] || {};
    const selectedId =
      values[param.key] != null && values[param.key] !== ""
        ? String(values[param.key])
        : "";
    const hasCourses = (courses || []).length > 0;

    return (
      <ParamsBlock
        key={param.key}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
      >
        {loadingFilters && (
          <Typography variant="body2" color="textSecondary">
            {t("student.reports.loadingDisciplines")}
          </Typography>
        )}
        {!loadingFilters && !hasCourses && (
          <Typography variant="body2" color="textSecondary">
            {t("student.reports.noDisciplines")}
          </Typography>
        )}
        {!loadingFilters && hasCourses && (
          <SearchableSelect
            label={param.label || t("common.labels.discipline")}
            options={courses || []}
            value={
              (courses || []).find(c => String(c.idTurmaDisc) === selectedId) ||
              null
            }
            disabled={isGenerating}
            disableClearable={param.required !== false}
            onChange={course => {
              this.setReportParam(
                report.id,
                param.key,
                course ? String(course.idTurmaDisc) : ""
              );
            }}
            getOptionLabel={o => o.name || ""}
            getOptionSelected={(a, b) =>
              String(a.idTurmaDisc) === String(b.idTurmaDisc)}
            noOptionsText={t("common.messages.noDiscipline")}
          />
        )}
      </ParamsBlock>
    );
  };

  renderRowParams = (report, isGenerating) => {
    const { t } = this.props;
    const params = reportUiParams(report, t);
    if (!params.length) return null;

    return params.map(param => {
      if (param.type === "discipline") {
        return this.renderDisciplineParam(report, param, isGenerating);
      }
      return null;
    });
  };

  render() {
    const { match, jobsState, t } = this.props;
    const { studentId } = match.params;
    const {
      loadingFilters,
      loadingReports,
      reports,
      generatingByReportId
    } = this.state;
    const jobs = (jobsState && jobsState.jobs) || [];
    const loading = loadingFilters || loadingReports;

    return (
      <Grid container spacing={2}>
        <BackButton to={`/student/${studentId}`} />
        <Grid item xs={12}>
          <Group>
            <Box pb={3} pt={2}>
              <Heading level={1} icon="insert_chart" gutterBottom>
                {t("student.reports.title")}
              </Heading>
              <Typography variant="body2" color="textSecondary">
                {t("student.reports.help")}
              </Typography>
            </Box>
            {loading ? (
              <LoadContainer>
                <CircularProgress />
              </LoadContainer>
            ) : (
              <TableGrid>
                {(reports || []).map(report => {
                  const isGenerating = isReportGenerating(
                    report.id,
                    generatingByReportId,
                    jobs
                  );
                  const uiParams = reportUiParams(report, t);
                  return (
                    <TableGrid.Row
                      key={report.id}
                      reportButton={
                        isGenerating ? (
                          <CircularProgress size={22} color="inherit" />
                        ) : (
                          true
                        )
                      }
                      button={!isGenerating}
                      onClick={
                        isGenerating
                          ? undefined
                          : () => this.onSelectItem(report)
                      }
                    >
                      <TableGrid.Title xs={12}>
                        <RowBody $dimmed={isGenerating}>
                          <TitleBlock>
                            <ReportTitle>
                              <ReportIcon
                                $color={report.iconColor || "#0d9488"}
                              >
                                {report.icon || "insert_chart"}
                              </ReportIcon>
                              <Box
                                component="span"
                                style={{
                                  fontSize: 18,
                                  fontWeight: 500,
                                  color: "#00303b"
                                }}
                              >
                                {report.name}
                              </Box>
                            </ReportTitle>
                            {isGenerating && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {t("student.reports.generating")}
                              </Typography>
                            )}
                            {!isGenerating && !uiParams.length && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {t("student.reports.noParams")}
                              </Typography>
                            )}
                          </TitleBlock>
                          {this.renderRowParams(report, isGenerating)}
                        </RowBody>
                      </TableGrid.Title>
                    </TableGrid.Row>
                  );
                })}
              </TableGrid>
            )}
            {!loading && (!reports || !reports.length) ? (
              <Box p={2} color="text.secondary">
                {t("student.reports.empty")}
              </Box>
            ) : null}
          </Group>
        </Grid>
      </Grid>
    );
  }
}

StudentReports.defaultProps = {
  jobsState: { jobs: [] }
};

StudentReports.propTypes = {
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      studentId: PropTypes.string
    })
  }).isRequired,
  jobsState: PropTypes.shape({
    jobs: PropTypes.arrayOf(PropTypes.shape())
  }),
  t: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  jobsState: state.jobsState
});

export default connect(mapStateToProps)(
  withRouter(withAlert()(withTranslation()(StudentReports)))
);
